import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import type { ClientSummary } from '@/features/Clients/Data';
import type { NivelAlertaMovimentacao, StatusSolicitacao } from '../../../models';
import { STATUS_SOLICITACAO_LABELS } from '../solicitacoesListPage.definitions';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_MOVIMENTACAO_LABELS } from '../../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import { FLUXO_MOVIMENTACAO_LABELS, NIVEL_ALERTA_LABELS } from '../solicitacoesResumo.utils';
import type { ContagensResumo, SelecaoResumo } from '../solicitacoesResumo.utils';
import ProjetoFiltroDropdown from './ProjetoFiltroDropdown';
import ResumoFacetaSecao from './ResumoFacetaSecao';
import type { VisaoControle } from '../solicitacoesLista.utils';

interface SolicitacoesResumoCardsProps {
  /** Quantas cada opção traz considerando os demais filtros, menos o próprio critério (ver
   * contarResumo) — assim uma opção desmarcada mostra quantas viriam ao marcá-la. */
  contagens: ContagensResumo;
  selecao: SelecaoResumo;
  onChange: (parcial: Partial<SelecaoResumo>) => void;
  clientes: ClientSummary[];
  /** Números do "Listar por": total da aba sem filtros, fora canceladas/excluídas. */
  totaisAtivos: Record<VisaoControle, number>;
  /** Listar por Movimentação ou por Solicitação — os dois números do card "Todos". */
  visao: VisaoControle;
  onVisao: (visao: VisaoControle) => void;
}

/** Mesma família de cor de STATUS_SOLICITACAO_BADGE_CLASS, em tom mais forte pra bolinha. */
const PONTO_STATUS: Record<StatusSolicitacao, string> = {
  aberta: 'bg-yellow-400',
  em_andamento: 'bg-blue-500',
  concluida: 'bg-green-500',
  cancelada: 'bg-gray-400',
  excluida: 'bg-red-500',
};

/** Mesma família de cor de CLASSE_COR_POR_NIVEL_ALERTA. */
const PONTO_NIVEL_ALERTA: Record<NivelAlertaMovimentacao, string> = {
  urgente: 'bg-red-500',
  atencao: 'bg-orange-400',
  neutro: 'bg-gray-300',
  sucesso: 'bg-green-500',
  cancelado: 'bg-gray-400',
};

const TEXTO_AJUDA_CONTAGEM =
  'Os números de movimentações e de solicitações podem ser diferentes: cada solicitação pode ter mais de uma ' +
  'movimentação (ex.: Troca de Projeto tem envio e devolução), e Formatação/Acessos não têm nenhuma. ' +
  'Todos os números do resumo contam na opção escolhida aqui (movimentações ou solicitações).';

const CHAVE_EXPANDIDO = 'si:solicitacoesResumoExpandido';

/** Recolhido por padrão (pedido do usuário) — só abre se a pessoa já tiver aberto antes. */
function lerExpandido(): boolean {
  try {
    return localStorage.getItem(CHAVE_EXPANDIDO) === 'true';
  } catch {
    return false;
  }
}

function alternar<T>(lista: T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

/**
 * Painel de resumo que também é o filtro (no estilo do GLPI), tudo num bloco só: status em
 * "placar" no topo (sempre visível) e, abaixo, colunas de Tipo / Movimentação / Urgência /
 * Etapa — recolhíveis, pra não pesar a tela de quem só quer o placar. Projeto fica no
 * cabeçalho como dropdown (dezenas de opções não cabem numa coluna). Tudo liga/desliga e
 * combina; nada selecionado num critério = sem filtro naquele critério.
 */
export default function SolicitacoesResumoCards({
  contagens,
  selecao,
  onChange,
  clientes,
  totaisAtivos,
  visao,
  onVisao,
}: SolicitacoesResumoCardsProps) {
  const [expandido, setExpandido] = useState(lerExpandido);
  const totalTodosStatus = Array.from(contagens.status.values()).reduce((soma, n) => soma + n, 0);
  const algumFiltro = Object.values(selecao).some((lista) => lista.length > 0);
  // Quando recolhido, avisa quantos critérios escondidos estão filtrando — senão a lista
  // "encolhia" sem motivo aparente.
  const filtrosOcultos = [selecao.tipos, selecao.fluxos, selecao.niveisAlerta, selecao.etapas].filter(
    (lista) => lista.length > 0
  ).length;

  const alternarExpandido = () => {
    setExpandido((atual) => {
      try {
        localStorage.setItem(CHAVE_EXPANDIDO, String(!atual));
      } catch {
        // não é crítico — só não persiste.
      }
      return !atual;
    });
  };

  const placar: Array<{ chave: StatusSolicitacao; label: string; quantidade: number; selecionado: boolean }> = (
    Object.keys(STATUS_SOLICITACAO_LABELS) as StatusSolicitacao[]
  ).map((status) => ({
    chave: status,
    label: STATUS_SOLICITACAO_LABELS[status],
    quantidade: contagens.status.get(status) ?? 0,
    selecionado: selecao.status.includes(status),
  }));
  const todosSelecionado = selecao.status.length === 0;

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="font-montserrat text-sm font-semibold text-gray-900">Resumo</h2>
        {/* Como a lista aparece (uma linha por Movimentação, na data dela, ou por Solicitação,
            na data da original) e a unidade de todos os números do painel. O número é o total
            da aba sem filtros, fora canceladas/excluídas. Trocar não mexe nos filtros. */}
        <div role="group" aria-label="Listar por" className="mr-auto flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-gray-500">Listar por</span>
          <span className="inline-flex rounded-md border border-gray-200 p-0.5">
            {(
              [
                { valor: 'movimentacoes', numero: totaisAtivos.movimentacoes, rotulo: 'Movimentações' },
                { valor: 'solicitacoes', numero: totaisAtivos.solicitacoes, rotulo: 'Solicitações' },
              ] as const
            ).map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                aria-pressed={visao === opcao.valor}
                onClick={() => onVisao(opcao.valor)}
                title={opcao.valor === 'movimentacoes' ? 'Uma linha por movimentação, na data dela' : 'Uma linha por solicitação, na data da movimentação original'}
                className={clsx(
                  'rounded px-2.5 py-1 text-xs font-medium transition',
                  visao === opcao.valor ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {opcao.rotulo} <span className="tabular-nums">({opcao.numero})</span>
              </button>
            ))}
          </span>
          <InformationCircleIcon className="h-4 w-4 text-gray-400" title={TEXTO_AJUDA_CONTAGEM} aria-label={TEXTO_AJUDA_CONTAGEM} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Projeto</span>
          <ProjetoFiltroDropdown
            clientes={clientes}
            contagem={contagens.clientes}
            selecionados={selecao.clientes}
            onChange={(clientesSelecionados) => onChange({ clientes: clientesSelecionados })}
          />
        </div>
        {algumFiltro && (
          <button
            type="button"
            onClick={() => onChange({ status: [], tipos: [], clientes: [], fluxos: [], etapas: [], niveisAlerta: [] })}
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </header>

      <div className="grid grid-cols-3 gap-px border-b border-gray-100 bg-gray-100 sm:grid-cols-6">
        {placar.map(({ chave, label, quantidade, selecionado }) => (
          <button
            key={chave}
            type="button"
            aria-pressed={selecionado}
            onClick={() => onChange({ status: alternar(selecao.status, chave) })}
            className={clsx(
              'relative flex flex-col items-start gap-0.5 px-4 py-3 text-left transition',
              selecionado ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
            )}
          >
            <span className={clsx('text-2xl font-semibold tabular-nums', selecionado ? 'text-blue-700' : 'text-gray-900')}>
              {quantidade}
            </span>
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <span className={clsx('h-2 w-2 rounded-full', PONTO_STATUS[chave])} />
              {label}
            </span>
            {selecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
          </button>
        ))}
        {/* Total de todos os status, na mesma unidade dos demais (os outros filtros continuam
            valendo); clicar desmarca os status. */}
        <button
          type="button"
          aria-pressed={todosSelecionado}
          onClick={() => onChange({ status: [] })}
          className={clsx(
            'relative flex flex-col items-start gap-0.5 px-4 py-3 text-left transition',
            todosSelecionado ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
          )}
        >
          <span className={clsx('text-2xl font-semibold tabular-nums', todosSelecionado ? 'text-blue-700' : 'text-gray-900')}>
            {totalTodosStatus}
          </span>
          <span className="text-xs font-medium text-gray-500">Todos</span>
          {todosSelecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
        </button>
      </div>

      {expandido && (
        <div className="space-y-2 px-4 py-3">
          <ResumoFacetaSecao
            titulo="Tipo"
            opcoes={Object.keys(TIPO_SOLICITACAO_LABELS) as Array<keyof typeof TIPO_SOLICITACAO_LABELS>}
            rotulos={TIPO_SOLICITACAO_LABELS}
            contagem={contagens.tipos}
            selecionados={selecao.tipos}
            onChange={(tipos) => onChange({ tipos })}
          />
          <ResumoFacetaSecao
            titulo="Movimentação"
            opcoes={Object.keys(FLUXO_MOVIMENTACAO_LABELS) as Array<keyof typeof FLUXO_MOVIMENTACAO_LABELS>}
            rotulos={FLUXO_MOVIMENTACAO_LABELS}
            contagem={contagens.fluxos}
            selecionados={selecao.fluxos}
            onChange={(fluxos) => onChange({ fluxos })}
          />
          <ResumoFacetaSecao
            titulo="Urgência"
            opcoes={Object.keys(NIVEL_ALERTA_LABELS) as NivelAlertaMovimentacao[]}
            rotulos={NIVEL_ALERTA_LABELS}
            contagem={contagens.niveisAlerta}
            selecionados={selecao.niveisAlerta}
            onChange={(niveisAlerta) => onChange({ niveisAlerta })}
            classePonto={PONTO_NIVEL_ALERTA}
          />
          <ResumoFacetaSecao
            titulo="Etapa"
            opcoes={Object.keys(STATUS_MOVIMENTACAO_LABELS) as Array<keyof typeof STATUS_MOVIMENTACAO_LABELS>}
            rotulos={STATUS_MOVIMENTACAO_LABELS}
            contagem={contagens.etapas}
            selecionados={selecao.etapas}
            onChange={(etapas) => onChange({ etapas })}
          />
        </div>
      )}

      <button
        type="button"
        onClick={alternarExpandido}
        className={clsx(
          'flex w-full items-center justify-center gap-1 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700',
          expandido && 'border-t border-gray-100'
        )}
      >
        {expandido ? 'Ocultar detalhes' : 'Mostrar detalhes (tipo, movimentação, urgência, etapa)'}
        {!expandido && filtrosOcultos > 0 && (
          <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white">{filtrosOcultos} ativo(s)</span>
        )}
        <ChevronDownIcon className={clsx('h-4 w-4 transition', expandido && 'rotate-180')} />
      </button>
    </section>
  );
}
